import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AutomationActionDenormalized,
  AutomationActionType,
  ChannelType,
  ConcreteExperienceMessageTeamsMessage,
  IExperience,
  ISupportingActor,
  SupportingActorType,
  TeamsErrors,
  TemplateResourceType
} from '@dadohr/shared/domainTypes/experience';
import {
  BackgroundCheckErrorType,
  DoceboSubtaskErrorType,
  DocusingerrorType,
  ErroDataType
} from '@dadohr/shared/domainTypes/instance';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { ReactNode } from 'react';
import { useRoutes } from '../../../../../routes';
import {
  getParticipantActor,
  getUserActorForKey
} from '../../../../../services/experiences/instance';
import { TimelineItemRef } from '../timelineItemId';
import { findParentTaskTemplate } from './taskTemplateLookup';
import {
  CCErrorData,
  ExperienceProblemData,
  InstanceErroWithData,
  SentItemErrorData
} from '../InstanceProblems';

export type { TimelineItemRef };

/**
 * Frontend-only mirror of automation-action error codes. `errorData` on
 * `AutomationActionDenormalized` is typed `any` everywhere in `@dadohr/shared`
 * (see plan risk #1) — this enum is the only place these codes are typed at
 * all, hand-kept in sync with whatever the backend actually writes.
 *
 * Moved here (was previously module-private inside ParticipantDialog.tsx) so
 * the taxonomy/resolver logic below can reference it without a circular
 * import back into ParticipantDialog.tsx.
 */
export enum ActionErrorType {
  NO_EVENT_FOUND = 'NO_EVENT_FOUND',
  NO_EMAIL = 'NO_EMAIL',
  EMAIL_NOT_SET = 'EMAIL_NOT_SET',
  GOOGLE_CALENDAR_API_ERROR = 'GOOGLE_CALENDAR_API_ERROR',
  NO_ACTOR = 'NO_ACTOR',
  UNABLE_TO_FIND_SLACK_USER = 'UNABLE_TO_FIND_SLACK_USER',
  NOT_IN_CHANNEL = 'NOT_IN_CHANNEL',
  CHANNEL_NOT_FOUND = 'CHANNEL_NOT_FOUND',
  CHANNEL_EXISTS_BUT_CANNOT_BE_ACCESSED_BY_BOT = 'CHANNEL_EXISTS_BUT_CANNOT_BE_ACCESSED_BY_BOT',
  GOOGLE_GROUPS_API_ERROR = 'GOOGLE_GROUPS_API_ERROR',
  ATLASSIAN_ACTOR_API_ERROR = 'ATLASSIAN_ACTOR_API_ERROR',
  ATLASSIAN_ACTOR_NOT_FOUND_ERROR = 'ATLASSIAN_ACTOR_NOT_FOUND_ERROR',
  ATLASSIAN_ACTOR_WITHOUT_EMAIL = 'ATLASSIAN_ACTOR_WITHOUT_EMAIL',
  ATLASSIAN_API_ERROR = 'ATLASSIAN_API_ERROR',
  ATLASSIAN_FIELDS_ERROR = 'ATLASSIAN_FIELDS_ERROR',
  ATLASSIAN_INSUFFICIENT_PERMISSION = 'ATLASSIAN_INSUFFICIENT_PERMISSION',
  ATLASSIAN_INVALID_AUTHENTICATION = 'ATLASSIAN_INVALID_AUTHENTICATION',
  MICROSOFT_CALENDAR_API_ERROR = 'MICROSOFT_CALENDAR_API_ERROR',
  MICROSOFT_GROUPS_API_ERROR = 'MICROSOFT_GROUPS_API_ERROR',
  NO_USER = 'NO_USER',
  PARTICIPANT_NOT_FOUND = 'PARTICIPANT_NOT_FOUND',
  ANCHOR_DATE_NOT_FOUND = 'ANCHOR_DATE_NOT_FOUND',
  NO_ONE_TO_ADD = 'NO_ONE_TO_ADD',
  NO_MICROSOFT_USER = 'NO_MICROSOFT_USER',
  MICROSOFT_CHANNEL_NOT_FOUND = 'MICROSOFT_CHANNEL_NOT_FOUND',
  MICROSOFT_USER_NOT_FOUND = 'MICROSOFT_USER_NOT_FOUND',
  MICROSOFT_USER_NOT_IN_TEAM = 'MICROSOFT_USER_NOT_IN_TEAM',
  OKTA_API_ERROR = 'OKTA_API_ERROR',
  OKTA_API_NO_USER = 'NO_OKTA_USER',
  OKTA_API_NO_GROUP = 'NO_OKTA_GROUP',
  OKTA_API_PERMISSION_DENIED = 'OKTA_PERMISSION_DENIED',
  ENROLL_IN_EXPERIENCE_ENROLLMENT_CRITERIA_NOT_MET = 'ENROLL_IN_EXPERIENCE_ENROLLMENT_CRITERIA_NOT_MET',
  ENROLL_IN_EXPERIENCE_NO_ANCHOR = 'ENROLL_IN_EXPERIENCE_NO_ANCHOR',
  ENROLL_IN_EXPERIENCE_EXPERIENCE_STOPPED = 'ENROLL_IN_EXPERIENCE_EXPERIENCE_STOPPED',
  ENROLL_IN_EXPERIENCE_EXPERIENCE_ARCHIVED = 'ENROLL_IN_EXPERIENCE_EXPERIENCE_ARCHIVED',
  ENROLL_IN_EXPERIENCE_ALREADY_ENROLLED = 'ENROLL_IN_EXPERIENCE_ALREADY_ENROLLED',
  ENROLL_IN_EXPERIENCE_TEMPLATE_NOT_FOUND = 'ENROLL_IN_EXPERIENCE_TEMPLATE_NOT_FOUND',
  ENROLL_IN_EXPERIENCE_TOO_FAR_IN_THE_PAST = 'ENROLL_IN_EXPERIENCE_TOO_FAR_IN_THE_PAST',
  ENROLL_IN_EXPERIENCE_ARCHIVE_FAILED = 'ENROLL_IN_EXPERIENCE_ARCHIVE_FAILED',
  CREATE_DOCUMENT_FROM_TEMPLATE_TEMPLATE_NOT_FOUND = 'CREATE_DOCUMENT_FROM_TEMPLATE_TEMPLATE_NOT_FOUND',
  CREATE_DOCUMENT_FROM_TEMPLATE_TARGET_RESOURCE_MISSING = 'CREATE_DOCUMENT_FROM_TEMPLATE_TARGET_RESOURCE_MISSING',
  CREATE_DOCUMENT_FROM_TEMPLATE_UNABLE_TO_CREATE_DOCUMENT = 'CREATE_DOCUMENT_FROM_TEMPLATE_UNABLE_TO_CREATE_DOCUMENT',
  // The Drive copy call returned without a file id.
  CREATE_DOCUMENT_FROM_TEMPLATE_COULD_NOT_COPY = 'COULD_NOT_COPY',
  // The create-document action's catch-all for a thrown Drive/Graph error.
  CREATE_DOCUMENT_FROM_TEMPLATE_UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  HTTP_POST_404_NOT_FOUND = 'HTTP_POST_404_NOT_FOUND',
  HTTP_POST_405_NOT_ALLOWED = 'HTTP_POST_405_NOT_ALLOWED',
  HTTP_POST_401_UNAUTHORIZED = 'HTTP_POST_401_UNAUTHORIZED',
  HTTP_POST_400_BAD_REQUEST = 'HTTP_POST_400_BAD_REQUEST',
  HTTP_POST_TIEMOUT = 'HTTP_POST_TIEMOUT',
  HTTP_POST_OTHER = 'HTTP_POST_OTHER',
  HTTP_POST_UNKNOWN = 'HTTP_POST_UNKNOWN',
  DOCEBO_NO_USER = 'NO_DOCEBO_USER',
  LESSONLY_NO_USER = 'NO_LESSONLY_USER'
}

/**
 * The shared taxonomy. One member per distinguishable "kind" of problem —
 * i.e. per branch that produces genuinely different copy in today's
 * cascades (`SuggestedActionForError`/`getActionErrorDetailsText` and their
 * siblings in `ParticipantDialog.tsx`). Granularity matches the original
 * `if (a.errorData?.type === X)` discriminant: sub-variations that today's
 * code only distinguishes deeper inside a branch (e.g. which email field is
 * missing, whether a Slack recipient email is on file) stay inside that
 * kind's copy function rather than becoming their own kind — same pattern
 * the ×N grouping in `group.ts` uses for Salesforce.
 *
 * `UNKNOWN` covers every case where today's code falls through to the
 * cascade's final `return null` (or, for `SuggestedActionForInstanceSubtaskError`
 * /`getSentItemErrorDetailsText`, an explicit `Unknown error` string) with no
 * kind-specific branch at all.
 *
 * --- Naming convention for new members ---
 * Name new kinds `<FAMILY>__<CASE>`: FAMILY matches the outer discriminant
 * (automation action type, provider, or subtask type); CASE is the specific
 * branch. Use `__` (double underscore) as the separator — FAMILY and CASE
 * segments often contain single underscores themselves, so `__` is the only
 * way to tell where one ends and the other begins. Kinds with no
 * sub-branching (e.g. UNENROLL_FROM_EXPERIENCE) skip the separator.
 *
 * Reuse these CASE suffixes for their established meaning rather than
 * inventing new wording for the same concept:
 *   - NO_ACTOR            Nobody is currently assigned to a required
 *                          supporting-actor role for THIS INSTANCE (the role
 *                          exists on the template). A role that isn't
 *                          defined on the template at all is a different,
 *                          rarer case — don't call that NO_ACTOR as well
 *                          (see CANNOT_ASSIGN_SUPPORTING_ACTOR__ACTOR_NOT_FOUND).
 *   - NO_EMAIL             The recipient exists but has no email address on
 *                          their user profile in Dado.
 *   - NO_<PROVIDER>_USER   This user exists in Dado, but no matching account
 *                          exists in the named third-party system — e.g.
 *                          NO_OKTA_USER, NO_SLACK_USER, NO_MICROSOFT_USER.
 *                          Always name the provider explicitly rather than a
 *                          generic phrase like "UNABLE_TO_FIND_USER".
 *   - API_ERROR            The backend has a genuine, distinct signal that
 *                          the third-party API itself failed. Only use this
 *                          if you've confirmed in the backend handler that
 *                          the raw error is specific to API/connectivity
 *                          failures — not a generic bucket that also catches
 *                          unrelated causes (in which case it isn't a real
 *                          API_ERROR case, just DEFAULT/UNKNOWN).
 *   - DEFAULT              This family's own fallback when no other CASE in
 *                          the family matches. Use the shared top-level
 *                          UNKNOWN instead if the family has no dedicated
 *                          fallback kind of its own.
 *
 * Before naming a new kind:
 *   1. Trace the actual backend condition that produces the raw error —
 *      don't name from the raw string alone. Backend code reuses and
 *      misnames raw error strings often (e.g. NO_MICROSOFT_USER and
 *      MICROSOFT_USER_NOT_FOUND turned out to mean the exact same thing
 *      across two different automations — the CASE suffix should reflect
 *      the real meaning, not preserve that inconsistency).
 *   2. Check whether an existing CASE suffix already covers the same
 *      concept elsewhere in this enum before inventing new wording.
 *   3. Don't collapse two kinds into the same suffix just because the
 *      words look similar — confirm they're the same root cause and fix,
 *      not just similarly worded (see ACTOR_NOT_FOUND vs NO_ACTOR above).
 */
export enum IssueKind {
  UNKNOWN = 'UNKNOWN',

  // --- AUTOMATION (AutomationActionDenormalized.errorData.type) ---
  ATLASSIAN_CREATE_ISSUE__INSUFFICIENT_PERMISSION = 'ATLASSIAN_CREATE_ISSUE__INSUFFICIENT_PERMISSION',
  ATLASSIAN_CREATE_ISSUE__ACTOR_WITHOUT_EMAIL = 'ATLASSIAN_CREATE_ISSUE__ACTOR_WITHOUT_EMAIL',
  ATLASSIAN_CREATE_ISSUE__NO_JIRA_USER = 'ATLASSIAN_CREATE_ISSUE__NO_JIRA_USER',
  ATLASSIAN_CREATE_ISSUE__FIELDS_ERROR = 'ATLASSIAN_CREATE_ISSUE__FIELDS_ERROR',
  ATLASSIAN_CREATE_ISSUE__API_ERROR = 'ATLASSIAN_CREATE_ISSUE__API_ERROR',
  ATLASSIAN_CREATE_ISSUE__DEFAULT = 'ATLASSIAN_CREATE_ISSUE__DEFAULT',

  ENROLL_IN_EXPERIENCE__CRITERIA_NOT_MET = 'ENROLL_IN_EXPERIENCE__CRITERIA_NOT_MET',
  ENROLL_IN_EXPERIENCE__NO_ANCHOR = 'ENROLL_IN_EXPERIENCE__NO_ANCHOR',
  ENROLL_IN_EXPERIENCE__EXPERIENCE_STOPPED = 'ENROLL_IN_EXPERIENCE__EXPERIENCE_STOPPED',
  ENROLL_IN_EXPERIENCE__EXPERIENCE_ARCHIVED = 'ENROLL_IN_EXPERIENCE__EXPERIENCE_ARCHIVED',
  ENROLL_IN_EXPERIENCE__ALREADY_ENROLLED = 'ENROLL_IN_EXPERIENCE__ALREADY_ENROLLED',
  ENROLL_IN_EXPERIENCE__TEMPLATE_NOT_FOUND = 'ENROLL_IN_EXPERIENCE__TEMPLATE_NOT_FOUND',
  ENROLL_IN_EXPERIENCE__TOO_FAR_IN_THE_PAST = 'ENROLL_IN_EXPERIENCE__TOO_FAR_IN_THE_PAST',
  ENROLL_IN_EXPERIENCE__ARCHIVE_FAILED = 'ENROLL_IN_EXPERIENCE__ARCHIVE_FAILED',
  ENROLL_IN_EXPERIENCE__DEFAULT = 'ENROLL_IN_EXPERIENCE__DEFAULT',

  GOOGLE_ADD_TO_GROUP__NO_ACTOR = 'GOOGLE_ADD_TO_GROUP__NO_ACTOR',
  GOOGLE_ADD_TO_GROUP__NO_EMAIL = 'GOOGLE_ADD_TO_GROUP__NO_EMAIL',
  GOOGLE_ADD_TO_GROUP__API_ERROR = 'GOOGLE_ADD_TO_GROUP__API_ERROR',
  GOOGLE_ADD_TO_GROUP__DEFAULT = 'GOOGLE_ADD_TO_GROUP__DEFAULT',

  OKTA_ADD_TO_GROUP__NO_ACTOR = 'OKTA_ADD_TO_GROUP__NO_ACTOR',
  OKTA_ADD_TO_GROUP__NO_EMAIL = 'OKTA_ADD_TO_GROUP__NO_EMAIL',
  OKTA_ADD_TO_GROUP__NO_OKTA_USER = 'OKTA_ADD_TO_GROUP__NO_OKTA_USER',
  OKTA_ADD_TO_GROUP__NO_GROUP = 'OKTA_ADD_TO_GROUP__NO_GROUP',
  OKTA_ADD_TO_GROUP__PERMISSION_DENIED = 'OKTA_ADD_TO_GROUP__PERMISSION_DENIED',
  OKTA_ADD_TO_GROUP__API_ERROR = 'OKTA_ADD_TO_GROUP__API_ERROR',
  OKTA_ADD_TO_GROUP__DEFAULT = 'OKTA_ADD_TO_GROUP__DEFAULT',

  GOOGLE_CALENDAR_EVENT_INVITATION__NO_EVENT_FOUND = 'GOOGLE_CALENDAR_EVENT_INVITATION__NO_EVENT_FOUND',
  GOOGLE_CALENDAR_EVENT_INVITATION__NO_EMAIL = 'GOOGLE_CALENDAR_EVENT_INVITATION__NO_EMAIL',
  GOOGLE_CALENDAR_EVENT_INVITATION__API_ERROR = 'GOOGLE_CALENDAR_EVENT_INVITATION__API_ERROR',
  GOOGLE_CALENDAR_EVENT_INVITATION__NO_ACTOR = 'GOOGLE_CALENDAR_EVENT_INVITATION__NO_ACTOR',

  MS_CALENDAR_EVENT_INVITATION__NO_EVENT_FOUND = 'MS_CALENDAR_EVENT_INVITATION__NO_EVENT_FOUND',
  MS_CALENDAR_EVENT_INVITATION__NO_EMAIL = 'MS_CALENDAR_EVENT_INVITATION__NO_EMAIL',
  MS_CALENDAR_EVENT_INVITATION__API_ERROR = 'MS_CALENDAR_EVENT_INVITATION__API_ERROR',
  MS_CALENDAR_EVENT_INVITATION__NO_ACTOR = 'MS_CALENDAR_EVENT_INVITATION__NO_ACTOR',

  MS_ADD_TO_GROUP__NO_ACTOR = 'MS_ADD_TO_GROUP__NO_ACTOR',
  MS_ADD_TO_GROUP__NO_EMAIL = 'MS_ADD_TO_GROUP__NO_EMAIL',
  MS_ADD_TO_GROUP__NO_MICROSOFT_USER = 'MS_ADD_TO_GROUP__NO_MICROSOFT_USER',
  // Add-to-group writes MICROSOFT_GROUPS_API_ERROR and calendar invitations
  // write MICROSOFT_CALENDAR_API_ERROR, but both come from the same Graph API,
  // so this kind accepts either.
  MS_ADD_TO_GROUP__API_ERROR = 'MS_ADD_TO_GROUP__API_ERROR',
  MS_ADD_TO_GROUP__DEFAULT = 'MS_ADD_TO_GROUP__DEFAULT',

  MS_TEAMS_INVITE_TO_CHANNEL__NO_ACTOR = 'MS_TEAMS_INVITE_TO_CHANNEL__NO_ACTOR',
  MS_TEAMS_INVITE_TO_CHANNEL__NO_EMAIL = 'MS_TEAMS_INVITE_TO_CHANNEL__NO_EMAIL',
  MS_TEAMS_INVITE_TO_CHANNEL__CHANNEL_NOT_FOUND = 'MS_TEAMS_INVITE_TO_CHANNEL__CHANNEL_NOT_FOUND',
  MS_TEAMS_INVITE_TO_CHANNEL__USER_NOT_IN_TEAM = 'MS_TEAMS_INVITE_TO_CHANNEL__USER_NOT_IN_TEAM',
  MS_TEAMS_INVITE_TO_CHANNEL__NO_MICROSOFT_USER = 'MS_TEAMS_INVITE_TO_CHANNEL__NO_MICROSOFT_USER',
  MS_TEAMS_INVITE_TO_CHANNEL__DEFAULT = 'MS_TEAMS_INVITE_TO_CHANNEL__DEFAULT',

  SLACK_INVITE_TO_CHANNEL__NO_EMAIL = 'SLACK_INVITE_TO_CHANNEL__NO_EMAIL',
  SLACK_INVITE_TO_CHANNEL__NO_SLACK_USER = 'SLACK_INVITE_TO_CHANNEL__NO_SLACK_USER',
  SLACK_INVITE_TO_CHANNEL__NOT_IN_CHANNEL = 'SLACK_INVITE_TO_CHANNEL__NOT_IN_CHANNEL',
  SLACK_INVITE_TO_CHANNEL__NO_ACTOR = 'SLACK_INVITE_TO_CHANNEL__NO_ACTOR',
  SLACK_INVITE_TO_CHANNEL__CHANNEL_NOT_FOUND = 'SLACK_INVITE_TO_CHANNEL__CHANNEL_NOT_FOUND',
  SLACK_INVITE_TO_CHANNEL__DEFAULT = 'SLACK_INVITE_TO_CHANNEL__DEFAULT',

  CREATE_DOCUMENT_FROM_TEMPLATE__TEMPLATE_NOT_FOUND = 'CREATE_DOCUMENT_FROM_TEMPLATE__TEMPLATE_NOT_FOUND',
  CREATE_DOCUMENT_FROM_TEMPLATE__TARGET_RESOURCE_MISSING = 'CREATE_DOCUMENT_FROM_TEMPLATE__TARGET_RESOURCE_MISSING',
  CREATE_DOCUMENT_FROM_TEMPLATE__UNABLE_TO_CREATE_DOCUMENT = 'CREATE_DOCUMENT_FROM_TEMPLATE__UNABLE_TO_CREATE_DOCUMENT',
  CREATE_DOCUMENT_FROM_TEMPLATE__API_ERROR = 'CREATE_DOCUMENT_FROM_TEMPLATE__API_ERROR',

  HTTP_POST__UNAUTHORIZED = 'HTTP_POST__UNAUTHORIZED',
  HTTP_POST__BAD_REQUEST = 'HTTP_POST__BAD_REQUEST',
  HTTP_POST__NOT_FOUND = 'HTTP_POST__NOT_FOUND',
  HTTP_POST__NOT_ALLOWED = 'HTTP_POST__NOT_ALLOWED',
  HTTP_POST__TIMEOUT = 'HTTP_POST__TIMEOUT',
  HTTP_POST__DEFAULT = 'HTTP_POST__DEFAULT',

  DOCEBO_ENROLL__NO_USER = 'DOCEBO_ENROLL__NO_USER',
  DOCEBO_ENROLL__NO_ACTOR = 'DOCEBO_ENROLL__NO_ACTOR',
  DOCEBO_ENROLL__NO_EMAIL = 'DOCEBO_ENROLL__NO_EMAIL',
  DOCEBO_ENROLL__DEFAULT = 'DOCEBO_ENROLL__DEFAULT',

  LESSONLY_ASSIGN__NO_USER = 'LESSONLY_ASSIGN__NO_USER',
  LESSONLY_ASSIGN__NO_ACTOR = 'LESSONLY_ASSIGN__NO_ACTOR',
  LESSONLY_ASSIGN__NO_EMAIL = 'LESSONLY_ASSIGN__NO_EMAIL',
  LESSONLY_ASSIGN__DEFAULT = 'LESSONLY_ASSIGN__DEFAULT',

  UNENROLL_FROM_EXPERIENCE = 'UNENROLL_FROM_EXPERIENCE',

  SYNC_TO_DATA_DESTINATION = 'SYNC_TO_DATA_DESTINATION',

  WORKDAY_TRIGGER_BUSINESS_PROCESS = 'WORKDAY_TRIGGER_BUSINESS_PROCESS',

  // --- SENT_ITEM (ConcreteExperienceMessage, keyed by provider) ---
  SENT_ITEM__POSTMARK_BOUNCED = 'SENT_ITEM__POSTMARK_BOUNCED',
  // ASSIGNEE_BOUNCE (below, separate family) is also a Postmark bounce on an
  // email send, recorded via a different code path (ExperienceProblemData
  // rather than ConcreteExperienceMessage). Placed here for visibility —
  // this may be a duplicate of SENT_ITEM__POSTMARK_BOUNCED rather than a
  // genuinely distinct case; not yet confirmed which, or whether both fire
  // for the same underlying bounce.
  ASSIGNEE_BOUNCE = 'ASSIGNEE_BOUNCE',
  SENT_ITEM__POSTMARK_INACTIVE_RECIPIENT = 'SENT_ITEM__POSTMARK_INACTIVE_RECIPIENT',
  SENT_ITEM__POSTMARK_NO_EMAIL = 'SENT_ITEM__POSTMARK_NO_EMAIL',
  SENT_ITEM__POSTMARK_DEFAULT = 'SENT_ITEM__POSTMARK_DEFAULT',
  SENT_ITEM__SLACK_USERS_NOT_FOUND = 'SENT_ITEM__SLACK_USERS_NOT_FOUND',
  SENT_ITEM__SLACK_CHANNEL_ACCESS = 'SENT_ITEM__SLACK_CHANNEL_ACCESS',
  SENT_ITEM__SLACK_DEFAULT = 'SENT_ITEM__SLACK_DEFAULT',
  SENT_ITEM__TEAMS_USER_NOT_FOUND = 'SENT_ITEM__TEAMS_USER_NOT_FOUND',
  SENT_ITEM__TEAMS_OTHER = 'SENT_ITEM__TEAMS_OTHER',
  SENT_ITEM__TEAMS_NO_ERROR = 'SENT_ITEM__TEAMS_NO_ERROR',
  SENT_ITEM__TWILIO_NO_PHONE_NUMBER = 'SENT_ITEM__TWILIO_NO_PHONE_NUMBER',
  SENT_ITEM__TWILIO_UNREACHABLE = 'SENT_ITEM__TWILIO_UNREACHABLE',
  SENT_ITEM__TWILIO_INVALID = 'SENT_ITEM__TWILIO_INVALID',
  SENT_ITEM__TWILIO_OTHER = 'SENT_ITEM__TWILIO_OTHER',
  SENT_ITEM__UNKNOWN = 'SENT_ITEM__UNKNOWN',

  // --- Instance-level errors (ErroDataType, InstanceError union) ---
  CANNOT_ASSIGN_SUPPORTING_ACTOR__INVALID_EMAIL = 'CANNOT_ASSIGN_SUPPORTING_ACTOR__INVALID_EMAIL',
  CANNOT_ASSIGN_SUPPORTING_ACTOR__USER_NOT_FOUND = 'CANNOT_ASSIGN_SUPPORTING_ACTOR__USER_NOT_FOUND',
  // Deliberately NOT named NO_ACTOR: this fires when the referenced actor key
  // isn't a role defined on the experience TEMPLATE at all (storageDispatch.ts:
  // `!tplDoc.data.supportingActors?.[actorKey]`) — a problem with the subtask
  // configuration on the template, not an unfilled instance-level assignment.
  // Contrast with CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_ACTOR below, which checks
  // the instance's own resolved actors and is the same "unfilled assignment"
  // concept as the automation family's NO_ACTOR kinds.
  //
  // Known gap, not handled specially: a subtask can reference a role that
  // existed on the template when the subtask was configured but was since
  // deleted/renamed — this kind doesn't distinguish that case from "never
  // existed." Left unhandled because deleting/renaming actors on a template
  // with running instances is rare; recording it here rather than fixing it.
  CANNOT_ASSIGN_SUPPORTING_ACTOR__ACTOR_NOT_FOUND = 'CANNOT_ASSIGN_SUPPORTING_ACTOR__ACTOR_NOT_FOUND',
  CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_EMAIL = 'CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_EMAIL',
  CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_ACTOR = 'CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_ACTOR',
  CANNOT_SEND_DOCUSIGN_ENVELOPE__MISSING_TEMPLATE = 'CANNOT_SEND_DOCUSIGN_ENVELOPE__MISSING_TEMPLATE',
  CANNOT_SEND_DOCUSIGN_ENVELOPE__ROLE_NAME_MISMATCH = 'CANNOT_SEND_DOCUSIGN_ENVELOPE__ROLE_NAME_MISMATCH',
  CANNOT_SEND_DOCUSIGN_ENVELOPE__FAULTY_OR_UNKNOWN = 'CANNOT_SEND_DOCUSIGN_ENVELOPE__FAULTY_OR_UNKNOWN',
  CANNOT_SEND_BACKGROUND_CHECK = 'CANNOT_SEND_BACKGROUND_CHECK',
  CANNOT_SEND_BACKGROUND_CHECK__MISSING_PERSONAL_EMAIL = 'CANNOT_SEND_BACKGROUND_CHECK__MISSING_PERSONAL_EMAIL',
  CANNOT_SEND_BACKGROUND_CHECK__PACKAGE_UNAVAILABLE = 'CANNOT_SEND_BACKGROUND_CHECK__PACKAGE_UNAVAILABLE',
  CANNOT_SEND_BACKGROUND_CHECK__MISSING_WORK_LOCATION = 'CANNOT_SEND_BACKGROUND_CHECK__MISSING_WORK_LOCATION',
  CANNOT_SEND_BACKGROUND_CHECK__UNRECOGNIZED_WORK_LOCATION = 'CANNOT_SEND_BACKGROUND_CHECK__UNRECOGNIZED_WORK_LOCATION',
  CANNOT_SEND_BACKGROUND_CHECK__WORK_LOCATION_FIELD_NOT_CONFIGURED = 'CANNOT_SEND_BACKGROUND_CHECK__WORK_LOCATION_FIELD_NOT_CONFIGURED',
  SALESFORCE_CONTACT_RESOLUTION_FAILED = 'SALESFORCE_CONTACT_RESOLUTION_FAILED',
  SALESFORCE_WRITE_FAILED = 'SALESFORCE_WRITE_FAILED',
  UKG_WRITE_FAILED = 'UKG_WRITE_FAILED',
  WORKDAY_WRITE_FAILED = 'WORKDAY_WRITE_FAILED',
  INSTANCE_SUBTASK__UNKNOWN = 'INSTANCE_SUBTASK__UNKNOWN',

  // --- COMPLETE_LMS_MODULE subtask (Docebo) ---
  COMPLETE_LMS_MODULE__NO_EMAIL = 'COMPLETE_LMS_MODULE__NO_EMAIL',
  COMPLETE_LMS_MODULE__NO_ACTOR = 'COMPLETE_LMS_MODULE__NO_ACTOR',
  COMPLETE_LMS_MODULE__NOT_A_DOCEBO_USER = 'COMPLETE_LMS_MODULE__NOT_A_DOCEBO_USER',
  COMPLETE_LMS_MODULE__ENROLL_FAILED = 'COMPLETE_LMS_MODULE__ENROLL_FAILED',
  COMPLETE_LMS_MODULE__UNIT_NOT_FOUND = 'COMPLETE_LMS_MODULE__UNIT_NOT_FOUND',
  COMPLETE_LMS_MODULE__INTEGRATION_BROKEN = 'COMPLETE_LMS_MODULE__INTEGRATION_BROKEN',
  COMPLETE_LMS_MODULE__UNKNOWN = 'COMPLETE_LMS_MODULE__UNKNOWN',

  // --- CC send errors ---
  CC__NO_EMAIL = 'CC__NO_EMAIL',
  CC__NO_ACTOR = 'CC__NO_ACTOR',
  CC__ROLE_EMAIL_BOUNCED = 'CC__ROLE_EMAIL_BOUNCED',
  CC__FIXED_EMAIL_BOUNCED = 'CC__FIXED_EMAIL_BOUNCED',

  // --- Missing data ---
  MISSING_ACTOR = 'MISSING_ACTOR',
  MISSING_VAR = 'MISSING_VAR',
  MISSING_DATA__UNKNOWN = 'MISSING_DATA__UNKNOWN'
}

export type IssueCopyContext = {
  t: (key: string, opts?: Record<string, unknown>) => string;
  supportingActors?: { [actorKey: string]: ISupportingActor };
  customUserFields?: CustomUserField[];
  onOpenParticipant?: (instance: Doc<IExperience>) => void;
  /** Opens the read-only dialog for a template timeline item over the
   * current view. Any consumer that renders `howToFixIt` must supply it:
   * the task/automation links in that copy have no route-based fallback and
   * render as plain text without it. Consumers that only read `summary`
   * can leave it out. */
  onOpenTimelineItem?: (ref: TimelineItemRef) => void;
  routes?: ReturnType<typeof useRoutes>['ROUTES'];
  /** Admin-facing label for a user field key such as `address` or
   * `customFields.region`. Lives in services/user, which this module can't
   * import (see below), so the page supplies it. */
  userFieldLabel?: (key: string) => string | undefined;
  /**
   * Injected rather than imported directly. Their real implementations live
   * in services/experiences/template.ts, components/Anchors/index.tsx, and
   * BackgroundCheckInstanceProblems.tsx — all of which transitively import
   * services/user/index.ts, which uses Vite-only `import.meta` syntax and
   * cannot be imported from this module without breaking it under this
   * repo's Jest config (ts-jest/node, no Vite transform). Following the
   * same "import-light, inject the rest" pattern already established for
   * services/experiences/clearAssigneeBounce.ts. The one real call site
   * (ParticipantDialog.tsx) already imports all of these directly and
   * threads them through; tests provide light stand-ins.
   */
  humanReadableActorName?: (
    actorKey: string,
    supportingActors?: { [actorKey: string]: ISupportingActor }
  ) => string;
  /** Real person name for an arbitrary actor key, when one has been resolved
   * for the given instance (see `resolveGroupActorNames.ts`). Returns
   * `undefined` for a non-user actor or an actor not yet resolved, in which
   * case callers fall back to `humanReadableActorName`'s role label. */
  resolveActorFullName?: (actorKey: string, instance: Doc<IExperience> | undefined) => string | undefined;
  // `t`/`errData` are loosely typed (`any`) rather than `UnsafeTFunction`/a
  // specific error shape: these fields exist so ParticipantDialog.tsx can
  // assign its real, precisely-typed implementations here, and a precise
  // parameter type would make that assignment contravariantly invalid.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getAnchorFields?: (customFields: CustomUserField[]) => { field: string; shortLabel: (t: any) => string }[];
  getBackgroundCheckDetail?: (args: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errData: any;
    supportingActors: { [actorKey: string]: ISupportingActor };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    t: any;
  }) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  BackgroundCheckSuggestedAction?: (props: { errData: any }) => ReactNode;
};

/** Raw shape for automation-action errors — used both by the wrapped
 * `AutomationActionErrorData` row (table/`getIssueDetailsText`) and by the
 * two secondary consumers that only ever have the bare action (no `instance`,
 * no `id`, no `tpl`). `instance` is optional for exactly that reason.
 *
 * `automationTplId`/`automationTitle` are only available when built from the
 * wrapped `AutomationActionErrorData` row; the two bare-action secondary
 * consumers never have them, so callers below must handle them being
 * undefined. */
export type AutomationIssueRaw = {
  family: 'AUTOMATION';
  action: AutomationActionDenormalized;
  instance?: Doc<IExperience>;
  automationTplId?: string;
  automationTitle?: string;
};

export type IssueRaw =
  | AutomationIssueRaw
  | { family: 'SENT_ITEM'; data: SentItemErrorData }
  | { family: 'ASSIGNEE_BOUNCE'; data: ExperienceProblemData & { type: 'ASSIGNEE_BOUNCE' } }
  | { family: 'INSTANCE_SUBTASK'; data: InstanceErroWithData }
  | { family: 'CC'; data: CCErrorData }
  | {
      family: 'MISSING_DATA';
      data: ExperienceProblemData & { type: 'MISSING_ACTOR' | 'MISSING_VAR' };
    };

export type Issue = {
  kind: IssueKind;
  groupKey: string;
  raw: IssueRaw;
  retryable: boolean;
  ctx: IssueCopyContext;
};

export type CopyPack = Record<
  IssueKind,
  {
    category: string;
    summary: (group: Issue[]) => string;
    whatCausedIt: (group: Issue[]) => ReactNode;
    howToFixIt: (group: Issue[]) => ReactNode;
  }
>;

// --------------------------------------------------------------------------
// Resolvers: raw error -> IssueKind. Each mirrors, condition for condition,
// the branch structure of the corresponding cascade(s) in ParticipantDialog.tsx
// (the two cascades don't always agree on which branches exist — see the
// per-kind comments in legacyCopy/ for the asymmetries that are preserved).
// --------------------------------------------------------------------------

export function resolveAutomationIssueKind(
  action: AutomationActionDenormalized
): IssueKind {
  // `AutomationActionDenormalized` is a union of 13 variants, not all of
  // which carry a typed `errorData`, so TS won't allow accessing it across
  // the whole union without narrowing per-branch. Neither cascade ever
  // handled Asana actions (see the `default` case below), so this is exactly
  // as safe as the original code's unchecked `a.errorData`.
  const anyAction = action as any;
  const errType = anyAction.errorData?.type as ActionErrorType | undefined;
  const data = anyAction.errorData?.data;

  // Cast the discriminant: UNENROLL_FROM_EXPERIENCE has no denormalized
  // variant at all (see AutomationActionDenormalized's union), so TS narrows
  // it out and calls the case below "not comparable" — it's unreachable in
  // practice, kept only so this switch stays total over `AutomationActionType`.
  switch (action.type as AutomationActionType) {
    case AutomationActionType.ATLASSIAN_CREATE_ISSUE:
      switch (errType) {
        case ActionErrorType.ATLASSIAN_INSUFFICIENT_PERMISSION:
          return IssueKind.ATLASSIAN_CREATE_ISSUE__INSUFFICIENT_PERMISSION;
        case ActionErrorType.ATLASSIAN_ACTOR_WITHOUT_EMAIL:
          return IssueKind.ATLASSIAN_CREATE_ISSUE__ACTOR_WITHOUT_EMAIL;
        case ActionErrorType.ATLASSIAN_ACTOR_NOT_FOUND_ERROR:
          return IssueKind.ATLASSIAN_CREATE_ISSUE__NO_JIRA_USER;
        case ActionErrorType.ATLASSIAN_FIELDS_ERROR:
          return IssueKind.ATLASSIAN_CREATE_ISSUE__FIELDS_ERROR;
        case ActionErrorType.ATLASSIAN_API_ERROR:
          return IssueKind.ATLASSIAN_CREATE_ISSUE__API_ERROR;
        default:
          return IssueKind.ATLASSIAN_CREATE_ISSUE__DEFAULT;
      }

    case AutomationActionType.ENROLL_IN_EXPERIENCE:
      switch (errType) {
        case ActionErrorType.ENROLL_IN_EXPERIENCE_ENROLLMENT_CRITERIA_NOT_MET:
          return IssueKind.ENROLL_IN_EXPERIENCE__CRITERIA_NOT_MET;
        case ActionErrorType.ENROLL_IN_EXPERIENCE_NO_ANCHOR:
          return IssueKind.ENROLL_IN_EXPERIENCE__NO_ANCHOR;
        case ActionErrorType.ENROLL_IN_EXPERIENCE_EXPERIENCE_STOPPED:
          return IssueKind.ENROLL_IN_EXPERIENCE__EXPERIENCE_STOPPED;
        case ActionErrorType.ENROLL_IN_EXPERIENCE_EXPERIENCE_ARCHIVED:
          return IssueKind.ENROLL_IN_EXPERIENCE__EXPERIENCE_ARCHIVED;
        case ActionErrorType.ENROLL_IN_EXPERIENCE_ALREADY_ENROLLED:
          return IssueKind.ENROLL_IN_EXPERIENCE__ALREADY_ENROLLED;
        case ActionErrorType.ENROLL_IN_EXPERIENCE_TEMPLATE_NOT_FOUND:
          return IssueKind.ENROLL_IN_EXPERIENCE__TEMPLATE_NOT_FOUND;
        case ActionErrorType.ENROLL_IN_EXPERIENCE_TOO_FAR_IN_THE_PAST:
          return IssueKind.ENROLL_IN_EXPERIENCE__TOO_FAR_IN_THE_PAST;
        case ActionErrorType.ENROLL_IN_EXPERIENCE_ARCHIVE_FAILED:
          return IssueKind.ENROLL_IN_EXPERIENCE__ARCHIVE_FAILED;
        default:
          return IssueKind.ENROLL_IN_EXPERIENCE__DEFAULT;
      }

    case AutomationActionType.GOOGLE_ADD_TO_GROUP:
      if (errType === ActionErrorType.NO_ACTOR) return IssueKind.GOOGLE_ADD_TO_GROUP__NO_ACTOR;
      if (errType === ActionErrorType.NO_EMAIL) return IssueKind.GOOGLE_ADD_TO_GROUP__NO_EMAIL;
      if (errType === ActionErrorType.GOOGLE_GROUPS_API_ERROR)
        return IssueKind.GOOGLE_ADD_TO_GROUP__API_ERROR;
      return IssueKind.GOOGLE_ADD_TO_GROUP__DEFAULT;

    case AutomationActionType.OKTA_ADD_TO_GROUP:
      if (errType === ActionErrorType.NO_ACTOR) return IssueKind.OKTA_ADD_TO_GROUP__NO_ACTOR;
      if (errType === ActionErrorType.NO_EMAIL) return IssueKind.OKTA_ADD_TO_GROUP__NO_EMAIL;
      if (errType === ActionErrorType.OKTA_API_NO_USER) return IssueKind.OKTA_ADD_TO_GROUP__NO_OKTA_USER;
      if (errType === ActionErrorType.OKTA_API_NO_GROUP) return IssueKind.OKTA_ADD_TO_GROUP__NO_GROUP;
      if (errType === ActionErrorType.OKTA_API_PERMISSION_DENIED)
        return IssueKind.OKTA_ADD_TO_GROUP__PERMISSION_DENIED;
      if (errType === ActionErrorType.OKTA_API_ERROR) return IssueKind.OKTA_ADD_TO_GROUP__API_ERROR;
      return IssueKind.OKTA_ADD_TO_GROUP__DEFAULT;

    case AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION:
      if (errType === ActionErrorType.NO_EVENT_FOUND)
        return IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__NO_EVENT_FOUND;
      if (errType === ActionErrorType.NO_EMAIL || errType === ActionErrorType.EMAIL_NOT_SET)
        return IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__NO_EMAIL;
      if (errType === ActionErrorType.GOOGLE_CALENDAR_API_ERROR)
        return IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__API_ERROR;
      if (errType === ActionErrorType.NO_ACTOR)
        return IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__NO_ACTOR;
      return IssueKind.UNKNOWN;

    case AutomationActionType.MS_OUTLOOK_CALENDER_EVENT_INVITATION:
      if (errType === ActionErrorType.NO_EVENT_FOUND)
        return IssueKind.MS_CALENDAR_EVENT_INVITATION__NO_EVENT_FOUND;
      if (errType === ActionErrorType.NO_EMAIL || errType === ActionErrorType.EMAIL_NOT_SET)
        return IssueKind.MS_CALENDAR_EVENT_INVITATION__NO_EMAIL;
      if (errType === ActionErrorType.MICROSOFT_CALENDAR_API_ERROR)
        return IssueKind.MS_CALENDAR_EVENT_INVITATION__API_ERROR;
      if (errType === ActionErrorType.NO_ACTOR)
        return IssueKind.MS_CALENDAR_EVENT_INVITATION__NO_ACTOR;
      return IssueKind.UNKNOWN;

    case AutomationActionType.MS_OUTLOOK_ADD_TO_GROUP:
      if (errType === ActionErrorType.NO_ACTOR) return IssueKind.MS_ADD_TO_GROUP__NO_ACTOR;
      if (errType === ActionErrorType.NO_EMAIL) return IssueKind.MS_ADD_TO_GROUP__NO_EMAIL;
      if (errType === ActionErrorType.NO_MICROSOFT_USER)
        return IssueKind.MS_ADD_TO_GROUP__NO_MICROSOFT_USER;
      if (
        errType === ActionErrorType.MICROSOFT_GROUPS_API_ERROR ||
        errType === ActionErrorType.MICROSOFT_CALENDAR_API_ERROR
      )
        return IssueKind.MS_ADD_TO_GROUP__API_ERROR;
      return IssueKind.MS_ADD_TO_GROUP__DEFAULT;

    case AutomationActionType.MS_TEAMS_INVITE_TO_CHANNEL:
      if (errType === ActionErrorType.NO_ACTOR)
        return IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__NO_ACTOR;
      if (errType === ActionErrorType.NO_EMAIL || errType === ActionErrorType.EMAIL_NOT_SET)
        return IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__NO_EMAIL;
      if (errType === ActionErrorType.MICROSOFT_CHANNEL_NOT_FOUND)
        return IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__CHANNEL_NOT_FOUND;
      if (errType === ActionErrorType.MICROSOFT_USER_NOT_IN_TEAM)
        return IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__USER_NOT_IN_TEAM;
      if (errType === ActionErrorType.MICROSOFT_USER_NOT_FOUND)
        return IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__NO_MICROSOFT_USER;
      return IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__DEFAULT;

    case AutomationActionType.SLACK_INVITE_TO_CHANNEL: {
      const slackErrCode = data?.slackErrCode;
      if (errType === ActionErrorType.NO_EMAIL || errType === ActionErrorType.EMAIL_NOT_SET)
        return IssueKind.SLACK_INVITE_TO_CHANNEL__NO_EMAIL;
      if (errType === ActionErrorType.UNABLE_TO_FIND_SLACK_USER)
        return IssueKind.SLACK_INVITE_TO_CHANNEL__NO_SLACK_USER;
      if (
        errType === ActionErrorType.NOT_IN_CHANNEL ||
        errType === ActionErrorType.CHANNEL_EXISTS_BUT_CANNOT_BE_ACCESSED_BY_BOT ||
        slackErrCode === 'not_in_channel'
      )
        return IssueKind.SLACK_INVITE_TO_CHANNEL__NOT_IN_CHANNEL;
      if (errType === ActionErrorType.NO_ACTOR) return IssueKind.SLACK_INVITE_TO_CHANNEL__NO_ACTOR;
      if (errType === ActionErrorType.CHANNEL_NOT_FOUND || slackErrCode === 'channel_not_found')
        return IssueKind.SLACK_INVITE_TO_CHANNEL__CHANNEL_NOT_FOUND;
      return IssueKind.SLACK_INVITE_TO_CHANNEL__DEFAULT;
    }

    case AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE:
      if (errType === ActionErrorType.CREATE_DOCUMENT_FROM_TEMPLATE_TEMPLATE_NOT_FOUND)
        return IssueKind.CREATE_DOCUMENT_FROM_TEMPLATE__TEMPLATE_NOT_FOUND;
      if (errType === ActionErrorType.CREATE_DOCUMENT_FROM_TEMPLATE_TARGET_RESOURCE_MISSING)
        return IssueKind.CREATE_DOCUMENT_FROM_TEMPLATE__TARGET_RESOURCE_MISSING;
      if (errType === ActionErrorType.CREATE_DOCUMENT_FROM_TEMPLATE_UNABLE_TO_CREATE_DOCUMENT)
        return IssueKind.CREATE_DOCUMENT_FROM_TEMPLATE__UNABLE_TO_CREATE_DOCUMENT;
      if (
        errType === ActionErrorType.CREATE_DOCUMENT_FROM_TEMPLATE_COULD_NOT_COPY ||
        errType === ActionErrorType.CREATE_DOCUMENT_FROM_TEMPLATE_UNKNOWN_ERROR
      )
        return IssueKind.CREATE_DOCUMENT_FROM_TEMPLATE__API_ERROR;
      return IssueKind.UNKNOWN;

    case AutomationActionType.HTTP_POST:
      switch (errType) {
        case ActionErrorType.HTTP_POST_401_UNAUTHORIZED:
          return IssueKind.HTTP_POST__UNAUTHORIZED;
        case ActionErrorType.HTTP_POST_400_BAD_REQUEST:
          return IssueKind.HTTP_POST__BAD_REQUEST;
        case ActionErrorType.HTTP_POST_404_NOT_FOUND:
          return IssueKind.HTTP_POST__NOT_FOUND;
        case ActionErrorType.HTTP_POST_405_NOT_ALLOWED:
          return IssueKind.HTTP_POST__NOT_ALLOWED;
        case ActionErrorType.HTTP_POST_TIEMOUT:
          return IssueKind.HTTP_POST__TIMEOUT;
        default:
          return IssueKind.HTTP_POST__DEFAULT;
      }

    case AutomationActionType.DOCEBO_ENROLL:
      if (errType === ActionErrorType.DOCEBO_NO_USER) return IssueKind.DOCEBO_ENROLL__NO_USER;
      if (errType === ActionErrorType.NO_ACTOR) return IssueKind.DOCEBO_ENROLL__NO_ACTOR;
      if (errType === ActionErrorType.NO_EMAIL) return IssueKind.DOCEBO_ENROLL__NO_EMAIL;
      return IssueKind.DOCEBO_ENROLL__DEFAULT;

    case AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT:
      if (errType === ActionErrorType.LESSONLY_NO_USER) return IssueKind.LESSONLY_ASSIGN__NO_USER;
      if (errType === ActionErrorType.NO_ACTOR) return IssueKind.LESSONLY_ASSIGN__NO_ACTOR;
      if (errType === ActionErrorType.NO_EMAIL) return IssueKind.LESSONLY_ASSIGN__NO_EMAIL;
      return IssueKind.LESSONLY_ASSIGN__DEFAULT;

    case AutomationActionType.UNENROLL_FROM_EXPERIENCE:
      return IssueKind.UNENROLL_FROM_EXPERIENCE;

    case AutomationActionType.SYNC_TO_DATA_DESTINATION:
      // Single branch in the cascades (no error-subtype switching) — one kind.
      return IssueKind.SYNC_TO_DATA_DESTINATION;

    case AutomationActionType.WORKDAY_TRIGGER_BUSINESS_PROCESS:
      // One kind; the copy explains the writer's error message.
      return IssueKind.WORKDAY_TRIGGER_BUSINESS_PROCESS;

    default:
      // ASANA_CREATE_TASK: neither cascade has a branch for this action type
      // today — both fall through to null.
      return IssueKind.UNKNOWN;
  }
}

export function resolveSentItemIssueKind(
  errData: SentItemErrorData,
  supportingActors: { [actorKey: string]: ISupportingActor }
): IssueKind {
  const { message, actor } = errData;
  const supportingActor = supportingActors[actor];

  if (message.provider === 'POSTMARK_EMAIL') {
    if (message.status === 'BOUNCED') return IssueKind.SENT_ITEM__POSTMARK_BOUNCED;
    if (message.error?.type === 'InactiveRecipient')
      return IssueKind.SENT_ITEM__POSTMARK_INACTIVE_RECIPIENT;
    if (message.error?.type === 'NoRecipient') return IssueKind.SENT_ITEM__POSTMARK_NO_EMAIL;
    return IssueKind.SENT_ITEM__POSTMARK_DEFAULT;
  }
  if (message.provider === 'SLACK') {
    if (message.error?.type === 'Slack') {
      if (message.error.details.includes('users_not_found'))
        return IssueKind.SENT_ITEM__SLACK_USERS_NOT_FOUND;
      if (
        supportingActor &&
        supportingActor.type === SupportingActorType.CHANNEL &&
        supportingActor.channeltype === ChannelType.SLACK
      )
        return IssueKind.SENT_ITEM__SLACK_CHANNEL_ACCESS;
    }
    return IssueKind.SENT_ITEM__SLACK_DEFAULT;
  }
  if (message.provider === 'TEAMS') {
    const teamsMessage = message as ConcreteExperienceMessageTeamsMessage;
    if (!teamsMessage.error) return IssueKind.SENT_ITEM__TEAMS_NO_ERROR;
    if (teamsMessage.error.errorCode === TeamsErrors.URSER_NOT_FOUD)
      return IssueKind.SENT_ITEM__TEAMS_USER_NOT_FOUND;
    return IssueKind.SENT_ITEM__TEAMS_OTHER;
  }
  if (message.provider === 'TWILIO') {
    const smsErrorCode = message.error?.details;
    if (smsErrorCode === 'NO_PHONE_NUMBER') return IssueKind.SENT_ITEM__TWILIO_NO_PHONE_NUMBER;
    if (smsErrorCode === '30003') return IssueKind.SENT_ITEM__TWILIO_UNREACHABLE;
    // 30005/30006: delivery-status codes (number unknown / landline). 21211 and
    // 21614: send-time rejections ('To' is not a valid number / not a mobile).
    if (
      smsErrorCode === '30005' ||
      smsErrorCode === '30006' ||
      smsErrorCode === '21211' ||
      smsErrorCode === '21614'
    )
      return IssueKind.SENT_ITEM__TWILIO_INVALID;
    return IssueKind.SENT_ITEM__TWILIO_OTHER;
  }
  return IssueKind.SENT_ITEM__UNKNOWN;
}

export function resolveInstanceSubtaskIssueKind(errData: InstanceErroWithData): IssueKind {
  if (errData.type === ErroDataType.CANNOT_ASSIGN_SUPPORTING_ACTOR) {
    if (errData.errorType === 'INVALID_EMAIL_ADDRESS')
      return IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__INVALID_EMAIL;
    if (errData.errorType === 'USER_NOT_FOUND')
      return IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__USER_NOT_FOUND;
    if (errData.errorType === 'SUPPORTING_ACTOR_NOT_FOUND')
      return IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__ACTOR_NOT_FOUND;
    return IssueKind.INSTANCE_SUBTASK__UNKNOWN;
  }
  if (errData.type === ErroDataType.CANNOT_SEND_DOCUSIGN_ENVELOPE) {
    switch (errData.errorType) {
      case DocusingerrorType.MISSING_EMAIL_ADRESS:
        return IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_EMAIL;
      case DocusingerrorType.SUPPORTING_ACTOR_NOT_FOUND: {
        // The linked resource is sometimes not actually a Docusign envelope
        // template (a data-integrity edge case, not a real "missing actor"
        // state) — route that mismatch to the generic FAULTY_OR_UNKNOWN kind
        // instead of NO_ACTOR, whose copy assumes a real envelope template
        // and a real missing signer role.
        const resource = errData.tpl.data.resources.find(
          (r) => r.resourceId === errData.resourceId
        );
        if (resource?.type !== TemplateResourceType.DOCUSIGN_ENVELOPE_TEMPLATE)
          return IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__FAULTY_OR_UNKNOWN;
        return IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_ACTOR;
      }
      case DocusingerrorType.MISSING_DOCUSIGN_TEMPLATE:
        return IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__MISSING_TEMPLATE;
      case DocusingerrorType.ROLE_NAME_MISMATCH:
        return IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__ROLE_NAME_MISMATCH;
      case DocusingerrorType.FAULTY_DADO_CONFIG:
      case DocusingerrorType.FAILED_TO_CREATE_ENVELOPE:
      case DocusingerrorType.UNKOWN:
        return IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__FAULTY_OR_UNKNOWN;
      default:
        return IssueKind.INSTANCE_SUBTASK__UNKNOWN;
    }
  }
  if (errData.type === ErroDataType.CANNOT_SEND_BACKGROUND_CHECK) {
    switch (errData.errorType) {
      case BackgroundCheckErrorType.MISSING_PERSONAL_EMAIL:
        return IssueKind.CANNOT_SEND_BACKGROUND_CHECK__MISSING_PERSONAL_EMAIL;
      case BackgroundCheckErrorType.PACKAGE_UNAVAILABLE:
        return IssueKind.CANNOT_SEND_BACKGROUND_CHECK__PACKAGE_UNAVAILABLE;
      case BackgroundCheckErrorType.MISSING_WORK_LOCATION:
        return IssueKind.CANNOT_SEND_BACKGROUND_CHECK__MISSING_WORK_LOCATION;
      case BackgroundCheckErrorType.UNRECOGNIZED_WORK_LOCATION:
        return IssueKind.CANNOT_SEND_BACKGROUND_CHECK__UNRECOGNIZED_WORK_LOCATION;
      case BackgroundCheckErrorType.WORK_LOCATION_FIELD_NOT_CONFIGURED:
        return IssueKind.CANNOT_SEND_BACKGROUND_CHECK__WORK_LOCATION_FIELD_NOT_CONFIGURED;
      default:
        return IssueKind.CANNOT_SEND_BACKGROUND_CHECK;
    }
  }
  if (errData.type === ErroDataType.CANNOT_COMPLETE_LMS_MODULE) {
    switch (errData.errorType) {
      case DoceboSubtaskErrorType.NO_EMAIL:
        return IssueKind.COMPLETE_LMS_MODULE__NO_EMAIL;
      case DoceboSubtaskErrorType.NO_ACTOR_IN_ROLE:
        return IssueKind.COMPLETE_LMS_MODULE__NO_ACTOR;
      case DoceboSubtaskErrorType.NOT_A_DOCEBO_USER:
        return IssueKind.COMPLETE_LMS_MODULE__NOT_A_DOCEBO_USER;
      case DoceboSubtaskErrorType.ENROLL_FAILED:
        return IssueKind.COMPLETE_LMS_MODULE__ENROLL_FAILED;
      case DoceboSubtaskErrorType.UNIT_NOT_FOUND:
        return IssueKind.COMPLETE_LMS_MODULE__UNIT_NOT_FOUND;
      case DoceboSubtaskErrorType.INTEGRATION_BROKEN:
        return IssueKind.COMPLETE_LMS_MODULE__INTEGRATION_BROKEN;
      default:
        return IssueKind.COMPLETE_LMS_MODULE__UNKNOWN;
    }
  }
  if (errData.type === ErroDataType.SALESFORCE_CONTACT_RESOLUTION_FAILED)
    return IssueKind.SALESFORCE_CONTACT_RESOLUTION_FAILED;
  if (errData.type === ErroDataType.SALESFORCE_WRITE_FAILED) return IssueKind.SALESFORCE_WRITE_FAILED;
  if (errData.type === ErroDataType.UKG_WRITE_FAILED) return IssueKind.UKG_WRITE_FAILED;
  if (errData.type === ErroDataType.WORKDAY_WRITE_FAILED) return IssueKind.WORKDAY_WRITE_FAILED;
  return IssueKind.INSTANCE_SUBTASK__UNKNOWN;
}

export function resolveCCIssueKind(errData: CCErrorData): IssueKind {
  if (errData.subtype === 'MISSING_EMAIL') return IssueKind.CC__NO_EMAIL;
  if (errData.subtype === 'NOT_ASSIGNED') return IssueKind.CC__NO_ACTOR;
  if (errData.subtype === 'BOUNCED')
    return errData.recipientType === 'ROLE' ? IssueKind.CC__ROLE_EMAIL_BOUNCED : IssueKind.CC__FIXED_EMAIL_BOUNCED;
  return IssueKind.UNKNOWN;
}

export function resolveMissingDataIssueKind(
  errData: ExperienceProblemData & { type: 'MISSING_ACTOR' | 'MISSING_VAR' }
): IssueKind {
  if (errData.type === 'MISSING_ACTOR') return IssueKind.MISSING_ACTOR;
  if (errData.type === 'MISSING_VAR') return IssueKind.MISSING_VAR;
  return IssueKind.MISSING_DATA__UNKNOWN;
}

/** Top-level resolver over the full `ExperienceProblemData` union — mirrors
 * `getIssueDetailsText`'s outer dispatch. */
export function resolveIssueKind(
  d: ExperienceProblemData,
  supportingActors: { [actorKey: string]: ISupportingActor } = {}
): IssueKind {
  if (d.type === 'AUTOMATION') return resolveAutomationIssueKind(d.denormalized);
  if (d.type === 'SENT_ITEM') return resolveSentItemIssueKind(d, supportingActors);
  if (d.type === 'MISSING_ACTOR' || d.type === 'MISSING_VAR') return resolveMissingDataIssueKind(d);
  if (d.type === 'CC_SEND_ERROR') return resolveCCIssueKind(d);
  if (d.type === 'ASSIGNEE_BOUNCE') return IssueKind.ASSIGNEE_BOUNCE;
  if (Object.values(ErroDataType).includes(d.type as ErroDataType))
    return resolveInstanceSubtaskIssueKind(d as InstanceErroWithData);
  return IssueKind.UNKNOWN;
}

// --------------------------------------------------------------------------
// Grouping keys — used only by the new-copy UI's grouping layer (`group.ts`)
// to bucket occurrences of the same underlying problem together. Not read by
// `legacyCopy.ts` or the legacy per-occurrence UI.
//
// Every kind's key is one of a handful of shapes:
//   - a direct field already on the raw error (action/subtask/timeline-item id)
//   - a raw, unresolved actor-role-key or email string, for kinds where
//     nothing ever resolves to a real user (the role isn't filled, or the
//     email is a static config value, not a user's)
//   - a userId resolved from an actor key via `getUserActorForKey`, or from
//     the instance's own participant via `getParticipantActor` (falls back
//     to the raw key string if resolution fails, never to `undefined`)
//   - a composite of two of the above, joined with `:`
// --------------------------------------------------------------------------

export const NO_ACTOR_AUTOMATION_KINDS = new Set<IssueKind>([
  IssueKind.GOOGLE_ADD_TO_GROUP__NO_ACTOR,
  IssueKind.OKTA_ADD_TO_GROUP__NO_ACTOR,
  IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__NO_ACTOR,
  IssueKind.MS_CALENDAR_EVENT_INVITATION__NO_ACTOR,
  IssueKind.MS_ADD_TO_GROUP__NO_ACTOR,
  IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__NO_ACTOR,
  IssueKind.SLACK_INVITE_TO_CHANNEL__NO_ACTOR,
  IssueKind.DOCEBO_ENROLL__NO_ACTOR,
  IssueKind.LESSONLY_ASSIGN__NO_ACTOR
]);

const AUTOMATION_PARENT_ID_KINDS = new Set<IssueKind>([
  IssueKind.ATLASSIAN_CREATE_ISSUE__API_ERROR,
  IssueKind.GOOGLE_ADD_TO_GROUP__API_ERROR,
  IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__API_ERROR,
  IssueKind.MS_ADD_TO_GROUP__API_ERROR,
  IssueKind.MS_CALENDAR_EVENT_INVITATION__API_ERROR,
  IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__DEFAULT,
  IssueKind.OKTA_ADD_TO_GROUP__API_ERROR,
  IssueKind.SLACK_INVITE_TO_CHANNEL__DEFAULT
]);

// Resolved via the automation error's actorKey.
export const AUTOMATION_USER_ID_KINDS = new Set<IssueKind>([
  IssueKind.ATLASSIAN_CREATE_ISSUE__ACTOR_WITHOUT_EMAIL,
  IssueKind.ATLASSIAN_CREATE_ISSUE__NO_JIRA_USER,
  IssueKind.GOOGLE_ADD_TO_GROUP__NO_EMAIL,
  IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__NO_EMAIL,
  IssueKind.MS_ADD_TO_GROUP__NO_EMAIL,
  IssueKind.MS_ADD_TO_GROUP__NO_MICROSOFT_USER,
  IssueKind.MS_CALENDAR_EVENT_INVITATION__NO_EMAIL,
  IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__NO_EMAIL,
  IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__NO_MICROSOFT_USER,
  IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__USER_NOT_IN_TEAM,
  IssueKind.OKTA_ADD_TO_GROUP__NO_EMAIL,
  IssueKind.OKTA_ADD_TO_GROUP__NO_OKTA_USER,
  IssueKind.SLACK_INVITE_TO_CHANNEL__NO_EMAIL,
  IssueKind.SLACK_INVITE_TO_CHANNEL__NO_SLACK_USER,
  IssueKind.DOCEBO_ENROLL__NO_EMAIL,
  IssueKind.DOCEBO_ENROLL__NO_USER,
  IssueKind.LESSONLY_ASSIGN__NO_EMAIL,
  IssueKind.LESSONLY_ASSIGN__NO_USER
]);

// SENT_ITEM kinds grouped by the raw Postmark recipient address
const SENT_ITEM_EMAIL_ADDRESS_KINDS = new Set<IssueKind>([
  IssueKind.SENT_ITEM__POSTMARK_BOUNCED,
  IssueKind.SENT_ITEM__POSTMARK_DEFAULT,
  IssueKind.SENT_ITEM__POSTMARK_INACTIVE_RECIPIENT
]);

// Rare "unknown error" fallback kinds with no item-level id on their raw
// shape at all — fall back to the instance id, so all occurrences for one
// instance collapse into a single group rather than staying ungrouped.
//
// SENT_ITEM's fallback kinds (TEAMS_NO_ERROR/UNKNOWN) are NOT here: unlike
// the kinds above, they do have an item-level id available
// (`SentItemErrorData.itemTplId`), so they use it like every other
// item-scoped kind instead of falling back this far.
const INSTANCE_ID_FALLBACK_KINDS = new Set<IssueKind>([
  IssueKind.MISSING_VAR,
  IssueKind.MISSING_DATA__UNKNOWN
]);

function getInstanceFromRaw(raw: IssueRaw): Doc<IExperience> | undefined {
  return raw.family === 'AUTOMATION' ? raw.instance : raw.data.instance;
}

/** Resolve an actor key to the actor's real userId via `getUserActorForKey`.
 * Actor key ≠ userId — the lookup can return `null` (a non-user actor like a
 * channel, or no matching entry in `instance.data.actors`). Falls back to
 * the raw actor key string itself rather than `undefined`, so a kind never
 * ends up with a broken/empty group key just because resolution failed. */
function resolveUserOrActorKey(
  instance: Doc<IExperience> | undefined,
  actorKey: string | undefined,
  fallbackId: string
): string {
  if (!actorKey) return fallbackId;
  if (!instance) return actorKey;
  return getUserActorForKey(instance, actorKey)?.userId || actorKey;
}

export function computeGroupKey(kind: IssueKind, raw: IssueRaw, fallbackId: string): string {
  if (INSTANCE_ID_FALLBACK_KINDS.has(kind)) {
    const instance = getInstanceFromRaw(raw);
    return instance?.id || fallbackId;
  }

  if (raw.family === 'AUTOMATION') {
    // Slack/Teams channel invites record the whole action actor as
    // `data.actionActor`; every other action records `data.actorKey`.
    const errData = (raw.action as any).errorData?.data;
    const actorKey: string | undefined = errData?.actorKey ?? errData?.actionActor?.key;
    const actionId = raw.action.templateId || fallbackId;
    if (NO_ACTOR_AUTOMATION_KINDS.has(kind) && actorKey) return `${actionId}:${actorKey}`;
    if (AUTOMATION_PARENT_ID_KINDS.has(kind)) return raw.automationTplId || actionId;
    if (AUTOMATION_USER_ID_KINDS.has(kind))
      return resolveUserOrActorKey(raw.instance, actorKey, actionId);
    // Enrolment failures group by the experience being enrolled into, so
    // every automation in this template that targets it shares one group.
    if (raw.action.type === AutomationActionType.ENROLL_IN_EXPERIENCE)
      return raw.action.tplToEnrollIn || actionId;
    // Default — covers ATLASSIAN_CREATE_ISSUE__FIELDS_ERROR (no fieldName
    // component needed) and CREATE_DOCUMENT_FROM_TEMPLATE__* (no literal
    // subtask-id field on this family).
    return actionId;
  }

  if (raw.family === 'SENT_ITEM') {
    const data = raw.data;
    if (SENT_ITEM_EMAIL_ADDRESS_KINDS.has(kind))
      return data.message.recipientEmail || fallbackId;
    if (kind === IssueKind.SENT_ITEM__SLACK_CHANNEL_ACCESS) return data.actor || fallbackId;
    if (kind === IssueKind.SENT_ITEM__TEAMS_NO_ERROR || kind === IssueKind.SENT_ITEM__UNKNOWN)
      return data.itemTplId || fallbackId;
    // Default: user ID via the message/task's actor key.
    return resolveUserOrActorKey(data.instance, data.actor, fallbackId);
  }

  if (raw.family === 'ASSIGNEE_BOUNCE') {
    // No actor key on this raw shape at all — always groups by the raw
    // bounced address.
    return raw.data.email || fallbackId;
  }

  if (raw.family === 'CC') {
    const data = raw.data;
    if (kind === IssueKind.CC__FIXED_EMAIL_BOUNCED) return data.email || fallbackId;
    if (kind === IssueKind.CC__NO_ACTOR) return `${data.itemTplId}:${data.recipientId}`;
    if (kind === IssueKind.CC__NO_EMAIL || kind === IssueKind.CC__ROLE_EMAIL_BOUNCED)
      return resolveUserOrActorKey(data.instance, data.recipientId, fallbackId);
    return data.itemTplId || fallbackId;
  }

  if (raw.family === 'MISSING_DATA') {
    // MISSING_ACTOR groups by the raw (unfilled) actor role key — there's no
    // user to resolve to by definition.
    const data = raw.data as { actor?: ISupportingActor };
    return data.actor?.key || fallbackId;
  }

  if (raw.family === 'INSTANCE_SUBTASK') {
    const data = raw.data as InstanceErroWithData & {
      actorKey?: string;
      workEmail?: string;
      taskTplId?: string;
    };
    if (kind === IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__ACTOR_NOT_FOUND)
      return data.actorKey || fallbackId;
    if (
      kind === IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__INVALID_EMAIL ||
      kind === IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__USER_NOT_FOUND
    )
      return data.workEmail || fallbackId;
    if (kind === IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_ACTOR) {
      const subtaskId = data.subtaskTplId || fallbackId;
      return data.actorKey ? `${subtaskId}:${data.actorKey}` : subtaskId;
    }
    if (kind === IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_EMAIL)
      return resolveUserOrActorKey(data.instance, data.actorKey, fallbackId);
    if (kind === IssueKind.COMPLETE_LMS_MODULE__NO_ACTOR) {
      const subtaskId = data.subtaskTplId || fallbackId;
      return data.actorKey ? `${subtaskId}:${data.actorKey}` : subtaskId;
    }
    if (
      kind === IssueKind.COMPLETE_LMS_MODULE__NO_EMAIL ||
      kind === IssueKind.COMPLETE_LMS_MODULE__NOT_A_DOCEBO_USER ||
      kind === IssueKind.COMPLETE_LMS_MODULE__ENROLL_FAILED
    )
      return resolveUserOrActorKey(data.instance, data.actorKey, fallbackId);
    if (kind === IssueKind.SALESFORCE_CONTACT_RESOLUTION_FAILED)
      // This error is instance-level (SF-9 hook is per-instance, not tied to
      // any timeline item), so there's no item id to key on — group by the
      // participant's userId alone. Functionally equivalent to grouping by
      // instance id (one participant per instance), but keying on userId
      // means the copy can name the affected person directly.
      return getParticipantActor(data.instance)?.userId || fallbackId;
    if (
      kind === IssueKind.SALESFORCE_WRITE_FAILED ||
      kind === IssueKind.UKG_WRITE_FAILED ||
      kind === IssueKind.WORKDAY_WRITE_FAILED
    ) {
      // A write queued by an automation has no subtask; group it by the
      // action, the way every other automation issue is grouped.
      if ('actionTplId' in data && data.actionTplId) return data.actionTplId;
      // Groups by the parent task id, not the old ×N collapse's
      // subtaskTplId+objectName+fieldName key — keeps this consistent with
      // every other kind's one-group-per-item shape. `taskTplId` is always
      // empty at the throw site — the parent task is only recoverable by
      // matching `subtaskTplId` against the template's task blocks, same as
      // `timelineItem`'s own resolution above.
      const taskTpl = findParentTaskTemplate(data.tpl, data.subtaskTplId);
      return taskTpl?.id || fallbackId;
    }
    // Every other MISSING_DATA kind (DocuSign's missing-template/role-mismatch
    // kinds, background-check, the generic instance-subtask-unknown kind) has no
    // special case above, so falls through to this: group by subtaskTplId.
    return data.subtaskTplId || fallbackId;
  }

  return fallbackId;
}

const NON_RETRYABLE_KINDS = new Set<IssueKind>([
  IssueKind.CC__NO_EMAIL,
  IssueKind.CC__NO_ACTOR,
  IssueKind.CC__ROLE_EMAIL_BOUNCED,
  IssueKind.CC__FIXED_EMAIL_BOUNCED,
  IssueKind.MISSING_ACTOR,
  IssueKind.MISSING_VAR,
  IssueKind.MISSING_DATA__UNKNOWN
]);

/** Best-effort, per the appendix table in the plan — matches today's actual
 * retry-button visibility/no-op behavior, not yet consumed by any Phase 1
 * call site. */
export function isRetryableKind(kind: IssueKind): boolean {
  return !NON_RETRYABLE_KINDS.has(kind);
}

// --------------------------------------------------------------------------
// Issue construction — the "thin lookup" entry points the rewired cascades
// in ../ParticipantDialog.tsx call: resolve a raw error to an IssueKind,
// normalize it into an Issue, then read text from a CopyPack for that kind.
// --------------------------------------------------------------------------

function toIssueRaw(d: ExperienceProblemData): IssueRaw {
  if (d.type === 'AUTOMATION')
    return {
      family: 'AUTOMATION',
      action: d.denormalized,
      instance: d.instance,
      automationTplId: d.automationTpl.id,
      automationTitle: d.automationTpl.title
    };
  if (d.type === 'SENT_ITEM') return { family: 'SENT_ITEM', data: d };
  if (d.type === 'MISSING_ACTOR' || d.type === 'MISSING_VAR') return { family: 'MISSING_DATA', data: d };
  if (d.type === 'CC_SEND_ERROR') return { family: 'CC', data: d };
  if (d.type === 'ASSIGNEE_BOUNCE') return { family: 'ASSIGNEE_BOUNCE', data: d };
  return { family: 'INSTANCE_SUBTASK', data: d as InstanceErroWithData };
}

/** Build a normalized `Issue` from any `ExperienceProblemData` member
 * (which includes `SentItemErrorData`, `CCErrorData`, `InstanceErroWithData`,
 * etc. individually, since each is one arm of that union). */
export function buildIssue(d: ExperienceProblemData, ctx: IssueCopyContext): Issue {
  const kind = resolveIssueKind(d, ctx.supportingActors);
  const raw = toIssueRaw(d);
  return {
    kind,
    groupKey: computeGroupKey(kind, raw, (d as { id?: string }).id || kind),
    raw,
    retryable: isRetryableKind(kind),
    ctx
  };
}

/** Automation issues are also resolved from a bare `AutomationActionDenormalized`
 * (no wrapping `AutomationActionErrorData` row) — the two secondary consumers
 * (`ExecutionReportTable/Automations.tsx`, `ParticipantDialogComponents/Automation.tsx`)
 * and `SuggestedActionForError` only ever have the bare action. */
export function buildAutomationIssue(
  action: AutomationActionDenormalized,
  instance: Doc<IExperience> | undefined,
  ctx: IssueCopyContext
): Issue {
  const raw: AutomationIssueRaw = { family: 'AUTOMATION', action, instance };
  const kind = resolveAutomationIssueKind(action);
  return {
    kind,
    groupKey: computeGroupKey(kind, raw, action.templateId),
    raw,
    retryable: isRetryableKind(kind),
    ctx
  };
}
